/**
 * ZeoWorkspaceDialog - Auswahl-Dialog der /zeo-Arbeitsoberflaeche.
 *   mode 'neu'/'klient': "Büro allgemein" ODER Klient suchen; danach optional Aufgabe waehlen oder + Neue Aufgabe;
 *                        [Chat starten] legt einen NEUEN Chat mit diesem Fokus an (onStartChat).
 *   mode 'aufgabe':       Aufgabe des gebundenen Klienten wechseln/loesen (onAufgabeGewaehlt).
 *   mode 'neue-aufgabe':  neue Aufgabe fuer den gebundenen Klienten (Dedup serverseitig), danach aktiv (onAufgabeAngelegt).
 * Zeigt NIE interne IDs -- nur Namen/Titel.
 */
import ZeoAufgabenSuche from './ZeoAufgabenSuche';
import { useEffect, useReducer, useState } from 'react';
import { betreuungAPI } from '@/api/betreuungAPI';
import { BUERO_ALLGEMEIN, startAuswahl, auswahlReducer, sichtbareTreffer, aktiveListe } from './zeoWorkspace';

function NeueAufgabeFeld({ klientId, sessionId, onFertig }) {
  const [titel, setTitel] = useState('');
  const [hinweis, setHinweis] = useState(null);
  const [duplikat, setDuplikat] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const anlegen = async () => {
    setLaeuft(true); setFehler(null); setHinweis(null); setDuplikat(null);
    try {
      const r = await betreuungAPI.zeoWorkspaceAufgabeAnlegen({ klient_id: klientId, session_id: sessionId || null, titel });
      if (r.status === 'duplikat') {
        // Der Hinweis MUSS sichtbar bleiben: erst nach Bestaetigung weiter (nichts wurde neu angelegt).
        setHinweis(r.hinweis);
        setDuplikat(r);
      } else {
        onFertig(r);
      }
    } catch (e) { setFehler(e.message); } finally { setLaeuft(false); }
  };

  return (
    <div className="mt-2 space-y-2">
      <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Titel der neuen Aufgabe"
        className="w-full text-sm border border-violet-100 rounded-lg px-3 py-2" data-testid="zeo-neue-aufgabe-titel" />
      {!duplikat && (
        <button type="button" disabled={!titel.trim() || laeuft} onClick={anlegen} data-testid="zeo-neue-aufgabe-anlegen"
          className="text-sm px-3 py-2 rounded-lg bg-violet-600 text-white disabled:opacity-40">Aufgabe anlegen</button>
      )}
      {hinweis && <p className="text-xs text-amber-700" data-testid="zeo-neue-aufgabe-duplikat">{hinweis}</p>}
      {duplikat && (
        <button type="button" onClick={() => onFertig(duplikat)} data-testid="zeo-neue-aufgabe-duplikat-ok"
          className="text-sm px-3 py-2 rounded-lg bg-violet-600 text-white">Verstanden – bestehende Aufgabe verwenden</button>
      )}
      {fehler && <p className="text-xs text-red-600">{fehler}</p>}
    </div>
  );
}

export default function ZeoWorkspaceDialog({
  mode, klientId: gebundenerKlientId, sessionId, aktuelleAufgabeId = '', aktuellerVorgangId = '',
  onClose, onStartChat, onAufgabeGewaehlt, onAufgabeAngelegt,
}) {
  const waehleKlient = mode === 'neu' || mode === 'klient';
  // Beim Wechseln im bestehenden Chat sind Aufgabe/Vorgang mit dem AKTUELLEN Fokus vorbelegt -- sonst wuerde das
  // Aendern nur der Aufgabe den Vorgang still loesen (Browser-E2E-Fund 07.10.2026).
  const [st, dispatch] = useReducer(auswahlReducer, undefined, () => startAuswahl(
    mode === 'aufgabe' ? { taskId: aktuelleAufgabeId, vorgangId: aktuellerVorgangId } : {}));
  const { suche, klient, taskId, vorgangId, neueAufgabe } = st;
  // Genau EINE aktive klient_id: im Wechsel-Dialog die gewaehlte, sonst die an den Chat gebundene.
  const aktiverKlientId = waehleKlient ? klient?.id : gebundenerKlientId;
  const treffer = sichtbareTreffer(st);
  const vorgaenge = aktiveListe(st.vorgaenge, aktiverKlientId);

  useEffect(() => {
    if (!waehleKlient || klient) return undefined;     // mit gewaehltem Klienten keine Suche mehr
    let abgebrochen = false;
    const t = setTimeout(() => {
      betreuungAPI.zeoWorkspaceKlienten(suche)
        .then((r) => { if (!abgebrochen) dispatch({ type: 'TREFFER_GELADEN', suche, liste: r.klienten || [] }); })
        .catch(() => {});
    }, 200);
    return () => { abgebrochen = true; clearTimeout(t); };
  }, [suche, waehleKlient, klient]);

  useEffect(() => {
    if (!aktiverKlientId) return undefined;
    let abgebrochen = false;      // spaete Antwort fuer einen frueheren Klienten darf nie in den aktuellen Zustand
    betreuungAPI.zeoWorkspaceVorgaenge(aktiverKlientId)
      .then((r) => { if (!abgebrochen) dispatch({ type: 'VORGAENGE_GELADEN', klientId: aktiverKlientId, liste: r.vorgaenge || [] }); })
      .catch(() => { if (!abgebrochen) dispatch({ type: 'VORGAENGE_GELADEN', klientId: aktiverKlientId, liste: [] }); });
    return () => { abgebrochen = true; };
  }, [aktiverKlientId]);

  const aufgabeAngelegt = (r) => {
    if (r.aufgabe?.id) dispatch({ type: 'TASK_GEWAEHLT', taskId: r.aufgabe.id });
    if (!waehleKlient) onAufgabeAngelegt?.(r);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="zeo-workspace-dialog">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-800">
            {waehleKlient ? (mode === 'neu' ? 'Neuer Chat' : 'Klient wechseln (neuer Chat)') : mode === 'aufgabe' ? 'Aufgabe / Vorgang wechseln' : 'Neue Aufgabe'}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Schließen">✕</button>
        </div>

        {waehleKlient && (
          <div data-testid="zeo-klient-auswahl">
            <p className="text-xs text-slate-500 mb-1">Klient</p>
            {klient ? (
              <div className="flex items-center justify-between w-full text-sm px-3 py-2 rounded-lg border border-violet-400 bg-violet-50" data-testid="zeo-klient-gewaehlt">
                <span>{klient.name}</span>
                <button type="button" onClick={() => dispatch({ type: 'KLIENT_ENTFERNT' })} data-testid="zeo-klient-aendern"
                  className="text-xs text-violet-700 hover:underline">ändern</button>
              </div>
            ) : (
              <>
                <button type="button" onClick={() => dispatch({ type: 'KLIENT_ENTFERNT' })} data-testid="zeo-klient-buero"
                  className="w-full text-left text-sm px-3 py-2 rounded-lg border border-violet-400 bg-violet-50 mb-2">
                  {BUERO_ALLGEMEIN}
                </button>
                <input value={suche} onChange={(e) => dispatch({ type: 'SUCHE', text: e.target.value })} placeholder="Klient suchen …" data-testid="zeo-klient-suche"
                  className="w-full text-sm border border-violet-100 rounded-lg px-3 py-2 mb-2" />
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {treffer.map((k) => (
                    <button key={k.id} type="button" onClick={() => dispatch({ type: 'KLIENT_GEWAEHLT', klient: k })} data-testid="zeo-klient-treffer"
                      className="w-full text-left text-sm px-3 py-1.5 rounded-lg border border-transparent hover:bg-violet-50">
                      {k.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {aktiverKlientId && (
          <div className="mt-4" data-testid="zeo-aufgabe-auswahl">
            <p className="text-xs text-slate-500 mb-1">Aufgabe (optional)</p>
            {mode !== 'neue-aufgabe' && (
              <ZeoAufgabenSuche klientId={aktiverKlientId} taskId={taskId} onWahl={(id) => dispatch({ type: 'TASK_GEWAEHLT', taskId: id })} />
            )}
            {(mode === 'neue-aufgabe' || neueAufgabe) ? (
              <NeueAufgabeFeld klientId={aktiverKlientId} sessionId={waehleKlient ? null : sessionId} onFertig={aufgabeAngelegt} />
            ) : (
              <button type="button" onClick={() => dispatch({ type: 'NEUE_AUFGABE_UMSCHALTEN', an: true })} data-testid="zeo-dialog-neue-aufgabe"
                className="mt-2 text-xs px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100">+ Neue Aufgabe</button>
            )}
          </div>
        )}

        {aktiverKlientId && mode !== 'neue-aufgabe' && vorgaenge.length > 0 && (
          <div className="mt-4" data-testid="zeo-vorgang-auswahl">
            <p className="text-xs text-slate-500 mb-1">Vorgang (optional)</p>
            <select value={vorgangId} onChange={(e) => dispatch({ type: 'VORGANG_GEWAEHLT', vorgangId: e.target.value })} data-testid="zeo-vorgang-select"
              className="w-full text-sm border border-violet-100 rounded-lg px-3 py-2">
              <option value="">kein Vorgang</option>
              {vorgaenge.map((v) => (<option key={v.id} value={v.id}>{v.titel}</option>))}
            </select>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Abbrechen</button>
          {waehleKlient && (
            <button type="button" onClick={() => onStartChat({ klientId: klient?.id || null, taskId: taskId || null, vorgangId: vorgangId || null })} data-testid="zeo-chat-starten"
              className="text-sm px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700">Chat starten</button>
          )}
          {mode === 'aufgabe' && (
            <button type="button" onClick={() => onAufgabeGewaehlt(taskId || null, vorgangId || null)} data-testid="zeo-aufgabe-uebernehmen"
              className="text-sm px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700">Übernehmen</button>
          )}
        </div>
      </div>
    </div>
  );
}
